import React, {useState, useRef, useEffect} from "react";
import {TransformWrapper, TransformComponent} from "react-zoom-pan-pinch";
import BasicButton, {CancelButton, ConfirmButton} from "./BasicButton";
import LoadingAnimation from "./LoadingAnimation";
import BasicModal, {BasicModalDismiss} from "./BasicModal";
import Spacer from "./Spacer";
import SelectLocationPictures from "../containers/SelectLocationPictures";
import FileUploader from "./FileUploader";

import {
    ATTR_OBJ_ID,
    ATTR_PICTURE_ROTATE,
    IMPORT_IMG_FILE_TYPES, IMPORT_VIDEO_FILE_TYPES,
    OBS_OBJ_LIST,
    STR_NO_VALUE,
    isSettingsView, ATTR_PICTURE_LOC_W, ATTR_PICTURE_LOC_H
} from "../prodect/ProsConnect";
import * as prosConnect from "../prodect/ProsConnect";
import PasteImageFromClipboard from "../prodect/PasteImageFromClipboard";
import {getCopyMoveManager} from "../prodect/CopyMoveManager";
import {NO_ZOOM, PROS_ZOOM_MAX_SCALE} from "./ImageWithMarker";
import prosAppNotif from "../prodect/ProsAppNotif";
import {prosPopupClose, prosPopupOpen} from "../prodect/ProsPopup";

export default function UiImage(props){
    const [imageLoaded, setImageLoaded] = useState(false)
    const [locationMarker, setLocationMarker] = useState(props.marker || null)
    const imgRef = useRef()

    useEffect(()=> {
        setLocationMarker(props.marker)
    }, [props.marker])

    const zoomInfo = props.item ? prosConnect.getPictureZoomInfo(props.item) : undefined
    const mayZoom = !getCopyMoveManager().getRearrangeMode() && !props.showFullImage && zoomInfo
    let useScale = calcScale(zoomInfo)
    let offsetX = zoomInfo ? calcOffset(useScale, zoomInfo.cx) : 0
    let offsetY = zoomInfo ? calcOffset(useScale, zoomInfo.cy) : 0

    let hasMarker = false
    let markerX
    let markerY
    if (isSettingsView()) {
        hasMarker = false
    } else if (locationMarker && locationMarker.inUse) {
        // Image edit - show marker position when editing
        hasMarker = true
        markerX = locationMarker.x
        markerY = locationMarker.y
    } else if (prosConnect.pictureHasMarker(props.item) &&
        prosConnect.pictureHasMarkerCoordinates(props.item)) {
        hasMarker = true
        // Small picture - get marker position from picture item
        markerX = parseFloat(props.item[ATTR_PICTURE_LOC_W])
        markerY = parseFloat(props.item[ATTR_PICTURE_LOC_H])
        // let scaleBefore = useScale
        while (useScale > 1) {
            // Optimize scale by zooming out if half of the marker does not fit into view
            // In relative dimensions size of the picture is "1" and size of the zoomed view is "1/scale"
            const x = offsetX + markerX     // to make marker position in scale of 0..1 within view
            const y = offsetY + markerY
            const fitsLeft  = x >= 0
            const fitsRight = x <= (1/useScale)
            const fitsTop   = y >= 0
            const fitsBottom= y <= (1/useScale)

            if (fitsLeft && fitsRight && fitsTop && fitsBottom) {
                break
            }

            useScale -= 0.5
            offsetX = calcOffset(useScale, zoomInfo.cx)
            offsetY = calcOffset(useScale, zoomInfo.cy)
        }
        if (useScale < 1) useScale = 1
    }

    // use a high resolution picture when zooming a lot
    const SCALE_TO_USE_HIGH_RESOLUTION_PICTURE = 2
    const src = (zoomInfo && zoomInfo.scale >= SCALE_TO_USE_HIGH_RESOLUTION_PICTURE) ?
        prosConnect.getItemImagePath(props.item) : props.imageSrc

    function calcScale() {
        if (!mayZoom) {return 1}
        if (zoomInfo.devOS === "W") {
            return zoomInfo.scale
        }
        let adjustFactor = 0.8
        if (!zoomInfo.imgH || !zoomInfo.viewW || !zoomInfo.imgW || !zoomInfo.viewW) {
            // Invalid size info - reduce scale
            adjustFactor = 0.5
        } else {
            // compare original image aspect ratio to saved zoomed view
            const imgAspectRatio = zoomInfo.imgW/(zoomInfo.imgH)
            const viewAspectRatio = zoomInfo.viewW/(zoomInfo.viewH)
            if (((imgAspectRatio >= 1) && (viewAspectRatio >= 1)) ||
                ((imgAspectRatio < 1) && (viewAspectRatio < 1))) {
                // same orientation - use default
            } else {
                if (imgAspectRatio < 1) {
                    adjustFactor *= imgAspectRatio
                }
                if (viewAspectRatio < 1) {
                    adjustFactor *= viewAspectRatio
                }
            }
        }
        const limitedScale = Math.min(Math.max(adjustFactor * zoomInfo.scale, 1), 10)
        // Round scale for 1 decimal
        return Math.round(limitedScale*10)/10
    }

    /**
     * Optimize / limit zoomed picture offset so that picture is utilizing visible view.
     * Compute new relative center 0..1 so that picture edge is aligned with view edge.
     * @param scale     Picture scaling factor 1..10 - also relative size of the image
     * @param cc        Relative picture center 0..1
     * @returns {number}    View offset so that full view is utilized
     */
    function calcOffset(scale, cc) {
        // ref document "Prodect kuvaobjekti.docx" // ((0.5/scale) – cc)
        if (!mayZoom) {return 0}
        const center = Number(cc)
        const relViewWidth = 1/scale
        const half = 0.5 * relViewWidth
        const offLow = center - half
        const offHigh = center + half
        let relOffset = -offLow
        if (offLow >= 1 - relViewWidth) {
            relOffset = relViewWidth - 1
        } else if (offHigh <= relViewWidth) {
            relOffset = 0
        }
        return relOffset
    }

    function onImageLoad(){
        if(imageLoaded) return
        setImageLoaded(true)
        if(typeof props.onImageLoad === "function"){
            props.onImageLoad({
                imgW: imgRef.current.naturalWidth,
                imgH: imgRef.current.naturalHeight,
                viewW: imgRef.current.width,
                viewH: imgRef.current.height,
            })
        }
    }

    function onImageError(evt){
        console.dir('onImageError', evt.target)
        if(props.onImageError) props.onImageError(evt)
    }

    function onImageClicked(evt) {
        if (props.onImageClick) {
            if(!imageLoaded) return
            evt.preventDefault()
            evt.stopPropagation()
            props.onImageClick()
        }
    }

    function renderImage(){
        let style = prosConnect.deepCopyObj(props.imageStyle) ?? {}
        if (!getCopyMoveManager().getRearrangeMode() && mayZoom) {
            style.transformOrigin = "0 0"
            style.transform = `scale(${useScale},${useScale}) translate(${offsetX*100}%,${offsetY*100}%)`
        }
        return <img
            id={props.imageId || null}
            ref={imgRef}
            src={src}
            alt={props.alt || 'Kuva'}
            loading={'lazy'}
            draggable={false}
            style={style}
            onLoad={() => onImageLoad()}
            onError={evt => onImageError(evt)}
            onClick={(evt) => onImageClicked(evt)}
        />
    }

    function renderLocationMarkers() {
        if (!hasMarker || (locationMarker && !locationMarker.inUse)) {
            return null
        }
        return <div
            style={{
                left: (markerX + offsetX) * useScale * 100 + '%',
                top: (markerY + offsetY) * useScale * 100 + '%'
            }}
            onClick={evt => evt.stopPropagation()}
            className={"marker marker-pin"}>
        </div>
    }

    function renderImageContent(){
        //errors will be done in future
        // if(error > 0) {
        //     if(error > 2) return <p className={"color-2nd"}>Virhe, kuvaa ei voi ladata.</p>
        //     return <div className={"display-flex-v flex-center width-100 padding"}>
        //         <BasicButton
        //             icon={"sync"}
        //             action={()=> {
        //                 setImageLoaded(false)
        //                 setError(error + 1)
        //                 setSrc(props.imageSrc)
        //             }}
        //             className={"margin-auto"}
        //         />
        //         <p className={"text-center text-sm width-100 margin-top" + (error > 1 ? " color-2nd" : "")}>Virhe kuvan lautauksessa</p>
        //     </div>
        // }
        if(!imageLoaded){
            return <figure className={"image-loader"}>
                {renderImage()}
                <LoadingAnimation color={"var(--color-light)"} />
            </figure>
        }
        // Note "props.imageMarkers" is for table view showing loc pictures below table
        // showing multiple markers for those OBS that has loc picture and match with table filter
        // and matching table filter
        return <>
            {renderImage()}
            {renderLocationMarkers()}
            {props.imageMarkers}
            {props.children}
        </>
    }

    return <figure
            onClick={evt => {
                if(!imageLoaded) return
                evt.stopPropagation()
                if(props.action) props.action(evt)
            }}
            className={props.className}
        >
        {renderImageContent()}
    </figure>
    //just a reminder that this exists
    //<KeepScale>{props.imageMarker}</KeepScale>
}

export function UiImageEditor(props){
    const [editMode, setEditMode] = useState(props.isMarkerPlacingEnabled || false)
    const [isPlacingMarker, setIsPlacingMarker] = useState(props.isMarkerPlacingEnabled || false)
    const [zoomCenter, setZoomCenter] = useState(props.zoomCenter)
    const compRef = useRef()

    useEffect(() => {
        const transformWrapper = compRef.current
        if (transformWrapper) {
            // todo not needed ? setZoomCenter(props.zoomCenter)
            transformWrapper.instance.setTransformState(props.zoomCenter.scale, props.zoomCenter.positionX, props.zoomCenter.positionY)
        }
    }, [props.zoomCenter])

    let mouseDownX = 0
    let mouseDownY = 0
    const mouse = {
        mouseDown: (evt)=> {
            mouseDownX = evt.clientX
            mouseDownY = evt.clientY
        },
        mouseUp: (evt)=>{
            // filter out click when
            // - mouse has moved between down&up click
            // - edited marker visibility, marker position or zoom center or
            // - or chosen to edit marker position
            if(hasMovedDuringClick(evt.clientX, evt.clientY)) return
            props.pictureClicked(evt, isPlacingMarker, hasEditedPicture())
        }
    }

    function handleTransform(evt){
        if (evt && evt.state) {
            const newZoomCenter = {scale: evt.state.scale, positionX: evt.state.positionX, positionY: evt.state.positionY}
            setZoomCenter(newZoomCenter)
        }
    }

    function hasMovedDuringClick(x, y){
        // If mouse moves this much while clicking, don't draw marker
        return x !== mouseDownX || y !== mouseDownY
    }

    /**
     * Reset image to original zoom and position or to full picture centered
     * @param fullSizeCentered  false: original zoom and focus, true: full picture
     */
    function resetTransforms(fullSizeCentered){
        const transformWrapper = compRef.current
        if (transformWrapper) {
            if (fullSizeCentered) {
                transformWrapper.resetTransform()
            }
            const zoomPos = fullSizeCentered ? NO_ZOOM: props.zoomCenter
            transformWrapper.instance.setTransformState(zoomPos.scale, zoomPos.positionX, zoomPos.positionY)
            setZoomCenter(zoomPos)
        }
    }

    function renderShowHideMarkerButton() {
        if(isSettingsView()) return null
        return <BasicButton
            btnClass={"primary"}
            icon={"image-tack"}
            action={() => {
                props.toggleShowMarker()
            }}
            disabled={isPlacingMarker}
            size={"md"}
            selected={!props.marker?.inUse && !isPlacingMarker}
            title={"Piilota sijaintimerkki"}
        />
    }

    function hasEditedPicture(){
        return JSON.stringify(props.initialMarker) !== JSON.stringify(props.marker) ||
            JSON.stringify(props.zoomCenter) !== JSON.stringify(zoomCenter)
    }

    function isEditing(){
        return (isPlacingMarker || hasEditedPicture()) && editMode
    }

    function renderMarkerButton(){
        if(isSettingsView()) return null
        const toggleEditMarkerButton = <BasicButton
            btnClass={"primary"}
            icon={"tack"}
            selected={isPlacingMarker}
            action={()=> setIsPlacingMarker(!isPlacingMarker)}
            size={"md"}
            title={"Aseta sijaintimerkki"}
        />
        return toggleEditMarkerButton
    }

    function saveImageEdits(){
        prosAppNotif("Kuvan asetukset (sijaintimerkki, kohdistus, zoom) tallennettu")
        setIsPlacingMarker(false)
        setEditMode(false)
        props.onSave(zoomCenter)
    }

    function cancelImageEdits(){
        setEditMode(false)
        setIsPlacingMarker(false)
        resetTransforms(false)
        props.onCancel()
    }

    function rendeSaveCancelButtons(){
        if (isEditing())
            return <div className={"btn-set"}>
                <ConfirmButton
                    action={()=> {
                        saveImageEdits()
                    }}
                    size={"md"}
                    shrink={"sm"}
                />
                <CancelButton
                    action={()=> {
                        cancelImageEdits()
                    }}
                    size={"md"}
                    shrink={"sm"}
                />
            </div>
        return null
    }

    function renderEditorButtons(){
        if(!editMode) return null
        return <div className={"btn-set margin-right"}>
            {renderShowHideMarkerButton()}
            {renderMarkerButton()}
            {props.imageEditControls}
        </div>
    }

    function renderEditButtons(){
        if(!prosConnect.hasPermissionTo("editObj", props.item)){
            return null
        }
        return isEditing() ? rendeSaveCancelButtons() : <BasicButton
            btnClass={"primary"}
            icon={"edit"}
            label={"muokkaa"}
            action={()=> setEditMode(!editMode)}
            selected={editMode}
            size={"md"}
        />
    }

    function openConfirmModal(){
        BasicModal('Vahvista', 'Tallenna kuvaan tehdyt muutokset?', {
            action: ()=> {
                saveImageEdits()
            },
            cancelCallback: ()=> {
                cancelImageEdits()
                props.minimizePicture()
            }
        })
    }

    const BtnSet =(props)=> {
        const instance = compRef.current
        if(!instance) return <LoadingAnimation />
        return <div
                className={"btn-set width-100 padding-sm"}
            >
            <BasicButton
                btnClass={"primary"}
                icon={"minimize"}
                action={()=> {
                    if (props.minimizePicture) {
                        if (hasEditedPicture() && isEditing()) {
                            // Minimize and not saved - prompt to confirm to save or discard changes
                            openConfirmModal()
                        } else {
                            props.minimizePicture()
                        }
                    } else {
                        // Location picture editor does not support minimize action - use that to reset zoom
                        resetTransforms(true)
                    }
                }}
                size={"md"}
            />
            <BasicButton
                btnClass={"primary"}
                icon={"zoom-in"}
                action={() => {
                    instance.zoomIn()
                }}
                size={"md"}
                disabled={zoomCenter.scale >= PROS_ZOOM_MAX_SCALE}
            />
            <BasicButton
                btnClass={"primary"}
                icon={"zoom-out"}
                action={() => {
                    instance.zoomOut()
                }}
                size={"md"}
                disabled={zoomCenter.scale <= 1}
            />
            <BasicButton
                btnClass={"primary"}
                icon={"size-full"}
                action={() => {
                    resetTransforms(true)
                }}
                size={"md"}
                title={"Koko kuva"}
                disabled={zoomCenter.scale <= 1}
            />
            {renderEditorButtons()}
            {renderEditButtons()}
        </div>
    }

    return <>
        <BtnSet minimizePicture={props.minimizePicture}/>
        <div
            className={"media media-image image-editor"}
        >
            <TransformWrapper
                ref={compRef}
                initialScale={1}
                onTransformed={(evt) => handleTransform(evt)}
                doubleClick={{disabled: true}}
            >
                <TransformComponent
                    minScale={1}
                    maxScale={PROS_ZOOM_MAX_SCALE}
                    limitToBounds={true}
                >
                    <div
                        onMouseDown={mouse.mouseDown}
                        onMouseUp={mouse.mouseUp}
                        className={"marker-frame"}
                    >{props.children}</div>
                </TransformComponent>
            </TransformWrapper>
        </div>
    </>
}



//Pros funcs and comps
export function RotatePicture(props) {
    let item = props.item
    let currenRotation = !item.hasOwnProperty(ATTR_PICTURE_ROTATE) || item[ATTR_PICTURE_ROTATE] === STR_NO_VALUE ?
        0 : parseInt(item[ATTR_PICTURE_ROTATE])
    const [rotateAngle, setRotateAngle] = useState(currenRotation)
    const [waitRotate, setWaitRotate] = useState(false)

    const saveRotatePicture = async ()=> {
        setWaitRotate(true)
        item[ATTR_PICTURE_ROTATE] = rotateAngle.toString()
        await prosConnect.getActiveProject().awaitUpdateItem(item, true)
        setTimeout(()=> {
            props.editCallback(item)
            setWaitRotate(false)
            BasicModalDismiss()
        }, 5000)//really long time but this feature is used rarely
    }

    function rotatePicture90Degrees() {
        setRotateAngle((rotateAngle + 90) % 360)
    }

    if (waitRotate) {
        return <section key={item.obj_id + '__rrp'}>
            <div className={"temp-cont"}>
                <LoadingAnimation text={"Hetki..."}/>
            </div>
        </section>
    }
    let imgStyle = {transform: 'translate(-50%, -50%) rotate(0deg)'};

    if (rotateAngle) {
        imgStyle.transform = 'translate(-50%, -50%) rotate(' + rotateAngle + 'deg)'
    }

    return <React.Fragment key={item.obj_id + '__rrp'}>
        <section>
            <UiImage
                imageSrc={prosConnect.getItemThumpPath(item)}
                imageStyle={imgStyle}
                className={"image-rotator"}
            />
            <div className={"btn-set_center"}>
                <BasicButton
                    btnClass={""}
                    label={"Käännä 90 astetta"}
                    action={() => {
                        rotatePicture90Degrees()
                    }}
                    icon={"rotate-cw"}
                />
            </div>
            <Spacer size={"lg"}/>
        </section>
        <footer className={"popup-footer"}>
            <div className={"btn-set"}>
                <CancelButton
                    action={BasicModalDismiss}
                />
                <ConfirmButton
                    action={async ()=> {
                        await saveRotatePicture(item)
                    }}
                />
            </div>
        </footer>
    </React.Fragment>
}


export function PictureImport({obs, callback, ...props}){
    const [clipBoardImportEnabled, setClipBoardImportEnabled] = useState(false)
    const isLocationPictureMode = isSettingsView() || callback

    function renderLocationPictureImportButton(){
        if(isLocationPictureMode || props.disableLocationPics) return null
        if(prosConnect.getActiveProject() && prosConnect.getActiveProject().getLocationPictures().length < 1) return null
        return <BasicButton
            btnClass={"2nd"}
            label={"Lisää sijaintikuva"}
            icon={"image-tack"}
            action={() => {
                BasicModalDismiss()
                setTimeout(()=> {
                    //Have to open new different popup as selecting loc pics inside BasicModal doesn't work.
                    prosPopupOpen(this,
                        'Valitse sijaintikuva',
                        renderLocationPictureImport(obs)
                    )
                }, 100)
            }}
        />
    }

    function renderLocationPictureImport(obs) {
        return <SelectLocationPictures
            close={()=> prosPopupClose()}
            addLocationPicture={ async (fileNameInAws, marker, zoomInfo)=> {
                const obsId = obs[ATTR_OBJ_ID]
                prosConnect.prosLog("add loc pict fileNameInAws:", fileNameInAws,
                    "obsId:", obsId, "marker:", marker, "zoom:", zoomInfo)
                let addPictAfterItem = (obs[OBS_OBJ_LIST] && obs[OBS_OBJ_LIST].length)
                    ? obs[OBS_OBJ_LIST][obs[OBS_OBJ_LIST].length-1] : obs
                const reportId = prosConnect.getObjListIdFromItemId(obsId)
                await prosConnect.getActiveProject()
                    .awaitAddPictLocItem(fileNameInAws, reportId, addPictAfterItem[ATTR_OBJ_ID], marker, zoomInfo)
                prosPopupClose()
            }}
        />
    }

    function renderClipBoardImport() {
        return <PasteImageFromClipboard
            addImageFromCanvas= {canvas => addImageFromCanvas(canvas)}
            cancelImport={()=>{
                setClipBoardImportEnabled(false)
            }}
        />
    }

    const  addImageFromCanvas = async (canvas)=> {
        BasicModalDismiss()
        if (canvas) {
            let dataURL = canvas.toDataURL()    // "data:image/png;base64,iV... "
            prosConnect.prosLogClearDt();
            let fileNameInAws = await prosConnect.uploadBase64(dataURL)
            prosConnect.prosLogDt('### base64 img uploaded', fileNameInAws)
            if (callback) {
                callback(fileNameInAws)
                return
            }
            let objId = prosConnect.getActiveProject().createPictureItem(fileNameInAws, obs)
            prosConnect.prosLogDt('### picture obj created', objId, fileNameInAws)
        }
    }

    let menuButtons =()=> <>
        <div className={"btn-list"}>
            <FileUploader
                item={obs}
                formats={isLocationPictureMode
                    ? IMPORT_IMG_FILE_TYPES : IMPORT_IMG_FILE_TYPES.concat(IMPORT_VIDEO_FILE_TYPES)}
                callback={BasicModalDismiss}
                fileNameCb={callback}
            />
            <BasicButton
                btnClass={"2nd"}
                label={"Leikepöydältä"}
                action={() => setClipBoardImportEnabled(true)}
                icon={"copy"}
            />
            {renderLocationPictureImportButton()}
        </div>
    </>

    let modalContent = null
    if(clipBoardImportEnabled) modalContent = renderClipBoardImport(obs)
    else modalContent = menuButtons()
    return modalContent
}